import { useId, useState, type ReactNode } from 'react';

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  /**
   * Which side the tooltip appears on relative to the trigger.
   * @default 'bottom'
   */
  side?: 'top' | 'bottom';
}

/**
 * Reusable accessible tooltip. Wraps any trigger element and shows
 * a styled label on hover or keyboard focus.
 *
 * Usage:
 *   <Tooltip content="Some label">
 *     <button>...</button>
 *   </Tooltip>
 */
export function Tooltip({ content, children, side = 'bottom' }: TooltipProps) {
  const id = useId();
  const [hidden, setHidden] = useState(false);

  const positionClasses = side === 'top' ? 'bottom-full mb-3' : 'top-full mt-3';

  return (
    <div
      className='group relative inline-flex'
      onClick={() => setHidden(true)}
      onMouseLeave={() => setHidden(false)}
    >
      {/* Spread aria-describedby onto the child via a wrapper span that
          forwards pointer/focus events upward. Using a wrapper avoids
          cloneElement which requires knowing the child's prop signature. */}
      <span aria-describedby={id} className='contents'>
        {children}
      </span>

      <span
        id={id}
        role='tooltip'
        className={[
          'pointer-events-none absolute left-1/2 z-50 -translate-x-1/2',
          positionClasses,
          'whitespace-nowrap rounded-md',
          'border border-border bg-surface px-2.5 pt-1 pb-2 text-xs font-medium text-fg',
          'shadow-elevated',
          'opacity-0 transition-opacity duration-150',
          hidden
            ? ''
            : 'group-hover:opacity-100 group-has-[:focus-visible]:opacity-100',
        ].join(' ')}
      >
        {content}
      </span>
    </div>
  );
}
